import { Briefcase, GraduationCap, Car, Plane, Coffee, Smartphone } from "lucide-react";

const useCases = [
  {
    icon: Briefcase,
    title: "Professional Items",
    description: "Protect your laptop bags, work equipment, business cards, and office supplies with digital identity for workplace peace of mind."
  },
  {
    icon: GraduationCap,
    title: "Student Essentials",
    description: "Keep textbooks, notebooks, backpacks, calculators, and study materials safe with QR technology designed for campus life."
  },
  {
    icon: Car,
    title: "Travel & Transport",
    description: "Secure your luggage, travel documents, metro cards, and transportation passes for worry-free journeys anywhere."
  },
  {
    icon: Smartphone,
    title: "Personal Electronics",
    description: "Safeguard phones, tablets, headphones, chargers, and gaming devices with instant owner connection technology."
  },
  {
    icon: Coffee,
    title: "Daily Essentials",
    description: "Never lose your keys, wallet, water bottles, glasses, and personal accessories again with smart QR protection."
  },
  {
    icon: Plane,
    title: "Adventure Gear",
    description: "Protect sports equipment, cameras, outdoor gear, and hobby items during your adventures and travels."
  }
];

export function AboutUseCasesSection() {
  // Generate ItemList Schema for SEO
  const useCasesSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Metefy QR Technology Use Cases",
    "description": "Comprehensive list of scenarios and items that can be protected using Metefy's digital identity QR technology",
    "numberOfItems": useCases.length,
    "itemListElement": useCases.map((useCase, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": useCase.title,
      "description": useCase.description,
      "url": "https://metefy.com/about#use-cases"
    }))
  };

  return (
    <section id="use-cases" className="py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-lg text-foreground font-semibold text-sm mb-6">
            <Briefcase className="w-4 h-4 text-foreground" />
            <span className="text-foreground">Use Cases</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
            Perfect for Every <span className="text-primary">Scenario</span>
          </h2>
          <p className="text-xl text-foreground/80 max-w-3xl mx-auto">
            From professional equipment to personal belongings, Metefy protects what matters most to you.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {useCases.map((useCase, index) => {
            const IconComponent = useCase.icon;
            return (
              <div key={index} className="group bg-card p-8 rounded-2xl border-2 border-border hover:border-primary transition-all duration-300 hover:shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-primary/10 rounded-lg p-2 group-hover:bg-primary/20 transition-all duration-300">
                    <IconComponent className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground">{useCase.title}</h3>
                </div>
                <p className="text-foreground/80 leading-relaxed">{useCase.description}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-16">
          <div className="bg-card rounded-2xl border-2 border-border p-8 text-center">
            <h3 className="text-2xl font-bold text-foreground mb-4">
              Don't See Your Use Case?
            </h3>
            <p className="text-foreground mb-6 max-w-2xl mx-auto">
              Metefy works for virtually any item you can attach a QR code to. From musical instruments to medical equipment, 
              our digital identity system adapts to your needs.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <span className="px-4 py-2 bg-primary/10 border border-primary/20 text-primary rounded-full text-sm font-medium">Musical Instruments</span>
              <span className="px-4 py-2 bg-primary/10 border border-primary/20 text-primary rounded-full text-sm font-medium">Medical Equipment</span>
              <span className="px-4 py-2 bg-primary/10 border border-primary/20 text-primary rounded-full text-sm font-medium">Art Supplies</span>
              <span className="px-4 py-2 bg-primary/10 border border-primary/20 text-primary rounded-full text-sm font-medium">Pet Accessories</span>
              <span className="px-4 py-2 bg-primary/10 border border-primary/20 text-primary rounded-full text-sm font-medium">Tools & Hardware</span>
            </div>
          </div>
        </div>
      </div>

      {/* Inject ItemList Schema for SEO - hidden from users */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(useCasesSchema) }}
        aria-hidden="true"
      ></script>
    </section>
  );
} 